import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontFamily, pdfFontSize, pdfSpacing } from '../theme';
import type { SchoolBranding } from '../types';
import LogoMark from './LogoMark';

/** One item in the letterhead's details line — "Student: Ayesha Khan", or a bare value ("Admission 2026-27"). */
export interface OfficialDetail {
  label?: string;
  value: string;
}

interface Props {
  branding: SchoolBranding;
  /** The document, set in italics under the school's name — "Fine Letter". */
  title: string;
  /** One line of who it's about — "Student: Ayesha Khan | Class: 6 | …". */
  details?: OfficialDetail[];
  /** For a half sheet (A5): a smaller logo and name, details two to a row. */
  compact?: boolean;
  /** Details per line (default 3) — fewer when each one is long. */
  perRow?: number;
}

/** Details in even rows, so a long line breaks evenly instead of leaving one item stranded. */
const rows = <T,>(items: T[], per: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / per) }, (_, r) => items.slice(r * per, r * per + per));

/** On a half sheet the first detail (who it's about) takes a line of its own, the rest go two to a line. */
const layout = <T,>(items: T[], compact?: boolean, perRow = 3): T[][] =>
  compact ? [...(items.length ? [items.slice(0, 1)] : []), ...rows(items.slice(1), 2)] : rows(items, perRow);

const styles = StyleSheet.create({
  wrap: { marginBottom: pdfSpacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingBottom: 10 },
  // The logo's mirror keeps the centred block centred on the page.
  side: { alignItems: 'center', justifyContent: 'center' },
  center: { flex: 1, alignItems: 'center', paddingHorizontal: pdfSpacing.sm },
  school: {
    fontFamily: pdfFontFamily.sans,
    fontSize: pdfFontSize.xxl,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#000',
    textAlign: 'center',
  },
  title: {
    fontFamily: pdfFontFamily.serifItalic,
    fontSize: 11.5,
    color: '#000',
    marginTop: 3,
  },
  meta: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', marginTop: 6 },
  metaNext: { marginTop: 3 },
  metaItem: { paddingHorizontal: 8, fontSize: 8.5, color: '#000' },
  metaRule: { borderLeftWidth: 0.6, borderLeftColor: '#333', borderLeftStyle: 'solid' },
  metaLabel: { fontWeight: 600 },
  rule: { height: 1.4, backgroundColor: pdfColors.textPrimary },
});

/**
 * The official letterhead the school's server-printed documents wear (marks
 * report, transcript, attendance sheet): logo left, the school's name
 * centred in capitals, the document's title in italics, one line of who it
 * concerns, and a single dark rule beneath.
 */
const OfficialHeader: React.FC<Props> = ({ branding, title, details = [], compact, perRow }) => {
  const logo = compact ? 42 : 54;
  const side = [styles.side, { width: logo, height: logo }];
  return (
    <View style={styles.wrap} wrap={false}>
      <View style={styles.row}>
        <View style={side}><LogoMark branding={branding} size={logo} /></View>
        <View style={styles.center}>
          <Text style={compact ? [styles.school, { fontSize: 13 }] : styles.school}>{branding.name}</Text>
          <Text style={styles.title}>{title}</Text>
          {layout(details, compact, perRow).map((row, r) => (
            <View key={r} style={[styles.meta, r ? styles.metaNext : {}]}>
              {row.map((d, i) => (
                <Text key={d.label ?? d.value} style={i ? [styles.metaItem, styles.metaRule] : styles.metaItem}>
                  {d.label && <Text style={styles.metaLabel}>{d.label}: </Text>}{d.value}
                </Text>
              ))}
            </View>
          ))}
        </View>
        <View style={side} />
      </View>
      <View style={styles.rule} />
    </View>
  );
};

export default OfficialHeader;
